import React, { useState } from 'react';
import { motion, AnimatePresence, useScroll, useSpring } from 'framer-motion';
import { Github, Linkedin, Mail } from 'lucide-react';

import Section from './components/Section';
import SkillBar from './components/SkillBar';
import Project from './components/Project';
import Experience from './components/Experience';
import Education from './components/Education';
import TabButton from './components/TabButton';
import ScreenshotPage from './components/ScreenshotPage';
import { colors } from './colors';
import portfolio from './portfolio.json';



const App = () => {
  const [activeTab, setActiveTab] = useState('skills');
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: colors.background, color: colors.text }}>
      <motion.div
        className="fixed top-0 left-0 right-0 h-1 transform origin-left"
        style={{ scaleX, backgroundColor: colors.accent }}
      />

      <div className="max-w-4xl mx-auto">
        <Section delay={0.2}>
          <motion.header
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h1 className="text-5xl font-bold mb-2" style={{ color: colors.primary }}>{portfolio.name}</h1>
            <p className="text-2xl" style={{ color: colors.secondary }}>{portfolio.title}</p>
          </motion.header>
        </Section>

        <Section delay={0.4}>
          <h2 className="text-3xl font-bold mb-4" style={{ color: colors.primary }}>About Me</h2>
          {portfolio.about.map((paragraph, index) => (
            <p key={index} className="text-lg mb-4">{paragraph}</p>
          ))}
        </Section>

        <Section delay={0.6}>
          <h2 className="text-3xl font-bold mb-4" style={{ color: colors.primary }}>Expertise</h2>
          <div className="mb-6">
            <div className="flex mb-4">
              <TabButton active={activeTab === 'skills'} onClick={() => setActiveTab('skills')}>Skills</TabButton>
              <TabButton active={activeTab === 'tools'} onClick={() => setActiveTab('tools')}>Tools & Technologies</TabButton>
            </div>
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
              >
                {activeTab === 'skills' && (
                  <>
                    {portfolio.skills.map((skill, index) => (
                      <SkillBar key={index} skill={skill.name} level={skill.level} color={colors.primary} />
                    ))}
                  </>
                )}
                {activeTab === 'tools' && (
                  <>
                    {portfolio.tools.map((tool, index) => (
                      <SkillBar key={index} skill={tool.name} level={tool.level} color={colors.secondary} />
                    ))}
                  </>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </Section>

        <Section delay={0.8}>
          <h2 className="text-3xl font-bold mb-4" style={{ color: colors.primary }}>Projects</h2>
          {portfolio.projects.map((project, index) => (
            <Project
              key={index}
              title={project.title}
              description={project.description}
              technologies={project.technologies}
              link={project.link}
              index={index}
            />
          ))}
        </Section>

        <ScreenshotPage projects={portfolio.projects} />

        <Section delay={1.0}>
          <h2 className="text-3xl font-bold mb-4" style={{ color: colors.primary }}>Experience</h2>
          {portfolio.experience.map((exp, index) => (
            <Experience
              key={index}
              role={exp.role}
              company={exp.company}
              period={exp.period}
              responsibilities={exp.responsibilities}
            />
          ))}
        </Section>

        <Section delay={1.2}>
          <h2 className="text-3xl font-bold mb-4" style={{ color: colors.primary }}>Education</h2>
          {portfolio.education.map((edu, index) => (
            <Education
              key={index}
              degree={edu.degree}
              institution={edu.institution}
              year={edu.year}
            />
          ))}
        </Section>

        <Section delay={1.4}>
          <h2 className="text-3xl font-bold mb-4" style={{ color: colors.primary }}>Get In Touch</h2>
          <div className="flex space-x-4">
            <a href={portfolio.contact.github} target="_blank" rel="noopener noreferrer">
              <Github size={24} className="text-gray-400 hover:text-white" />
            </a>
            <a href={portfolio.contact.linkedin} target="_blank" rel="noopener noreferrer">
              <Linkedin size={24} className="text-gray-400 hover:text-white" />
            </a>
            <a href={`mailto:${portfolio.contact.email}`}>
              <Mail size={24} className="text-gray-400 hover:text-white" />
            </a>
          </div>
        </Section>
      </div>
    </div>
  );
};

export default App;
